import { create } from "zustand";

/**
 * One pending pre-approval review (ADR-042). Stamped from the
 * `data-pre-approval-review` SSE event when the assistant calls
 * `request_commit_approval`, and re-stamped on cold reload via
 * threadHistoryProvider when a persisted ToolCallMessagePart with
 * `functionName === "request_commit_approval"` and empty Content is hydrated.
 *
 * Each entry's `previewId` matches the path component the sidebar fetches
 * (`/api/preview/{previewId}/pdf`); `toolCallId` is the URL the Approve / Reject
 * row POSTs to (`/api/chat/tool-call/{toolCallId}/result`).
 *
 * `arrivalSeq` is a monotonically increasing counter (per-process) that lets
 * the sidebar resolver pick the newest entry when several pending approvals
 * exist for the same thread, e.g. the assistant re-emitted `request_commit_approval`
 * after the user closed the sidebar without a verdict. Older entries should
 * never be the click target — they refer to a tool call the backend has already
 * forgotten and would return 410 on, surfacing as a misleading "saved" toast.
 */
export type PendingApprovalPreview = {
  previewId: string;
  toolCallId: string;
  threadId: string;
  arrivalSeq: number;
  summary?: string;
  pdfUrl: string;
  docxUrl: string;
  diffHtmlUrl?: string;
  action?: string;
  documentName?: string;
  praetorDocumentId?: string;
  /** Thread-local GeneratedDocument id — links the approval to the version stepper entry. */
  generatedDocumentId?: string;
};

/**
 * Input shape for `setPending` — same as `PendingApprovalPreview` minus
 * `arrivalSeq`, which the store stamps from its internal counter so callers
 * don't have to keep one of their own (SSE event handler, cold-reload hydrator).
 */
export type PendingApprovalInput = Omit<PendingApprovalPreview, "arrivalSeq">;

/**
 * Per-thread map of pending pre-approval reviews. Multiple approvals on the same
 * thread (e.g. the LLM made two `request_commit_approval` calls in the same turn)
 * are keyed by `previewId`; the sidebar shows whichever preview matches the
 * currently opened document.
 *
 * `setPending` evicts any existing entry on the same `threadId` with a different
 * `previewId` before adding the new one — only one approval is "live" per thread
 * at a time, and stale entries trip the false-positive-saved toast described above.
 * Cross-thread entries are preserved.
 */
type ApprovalState = {
  pendingByPreviewId: Record<string, PendingApprovalPreview>;
  setPending: (preview: PendingApprovalInput) => void;
  clearByPreviewId: (previewId: string) => void;
  clearByToolCallId: (toolCallId: string) => void;
  /** Wipes everything for a thread (used on thread delete / archive). */
  clearByThreadId: (threadId: string) => void;
};

let __arrivalSeqCounter = 0;

export const useApprovalStore = create<ApprovalState>((set) => ({
  pendingByPreviewId: {},
  setPending: (preview) =>
    set((state) => {
      const arrivalSeq = ++__arrivalSeqCounter;
      const next: Record<string, PendingApprovalPreview> = {};
      for (const [k, v] of Object.entries(state.pendingByPreviewId)) {
        // Evict any other live entry on the same thread — only one approval
        // is "live" per thread at a time. Same-previewId overwrite is fine
        // (the new entry below replaces it).
        if (v.threadId === preview.threadId && v.previewId !== preview.previewId) continue;
        next[k] = v;
      }
      next[preview.previewId] = { ...preview, arrivalSeq };
      return { pendingByPreviewId: next };
    }),
  clearByPreviewId: (previewId) =>
    set((state) => {
      if (!(previewId in state.pendingByPreviewId)) return state;
      const { [previewId]: _, ...rest } = state.pendingByPreviewId;
      return { pendingByPreviewId: rest };
    }),
  clearByToolCallId: (toolCallId) =>
    set((state) => {
      const next: Record<string, PendingApprovalPreview> = {};
      let changed = false;
      for (const [k, v] of Object.entries(state.pendingByPreviewId)) {
        if (v.toolCallId === toolCallId) { changed = true; continue; }
        next[k] = v;
      }
      return changed ? { pendingByPreviewId: next } : state;
    }),
  clearByThreadId: (threadId) =>
    set((state) => {
      const next: Record<string, PendingApprovalPreview> = {};
      let changed = false;
      for (const [k, v] of Object.entries(state.pendingByPreviewId)) {
        if (v.threadId === threadId) { changed = true; continue; }
        next[k] = v;
      }
      return changed ? { pendingByPreviewId: next } : state;
    }),
}));
